import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { onValue, ref, update } from 'firebase/database';
import { auth, db } from '../firebaseConfig';
import { applyPendingOnboarding, hasSavedScout, needsOnboarding, isBasicJobSearchPath } from './onboardingAccess';
import { clearCachedFirebaseData } from './firebaseDataCache';
import AuthScreen from './components/AuthScreen';
import SignupAmbassador from './components/SignupAmbassador';
import { buildProfileLocationUpdates } from './profile/profileLocation';
import {
  hasPendingOnboardingCompletion,
  processOnboardingCompletion,
  processPendingOnboardingCompletions,
  queueOnboardingCompletion,
} from './onboardingCompletionOutbox';
import JobSeekerShell from './components/JobSeekerShell';
import AskAnythingScreen from './screens/AskAnythingScreen';
import CareerCoachScreen from './screens/CareerCoachScreen';
import CareerPathsScreen from './screens/CareerPathsScreen';
import CompanyRadarScreen from './screens/CompanyRadarScreen';
import ConfigureScoutScreen from './screens/ConfigureScoutScreen';
import InboxScreen from './screens/InboxScreen';
import LearningPathwaysScreen from './screens/LearningPathwaysScreen';
import MockInterviewScreen from './screens/MockInterviewScreen';
import OnboardingScreen from './screens/OnboardingScreen';
import OpportunitiesScreen from './screens/OpportunitiesScreen';
import JobSearchScreen from './screens/JobSearchScreen';
import PublicJobApplicationScreen from './screens/PublicJobApplicationScreen';
import OutreachEmailCallback from './screens/OutreachEmailCallback';
import ProfileScreen from './screens/ProfileScreen';
import SettingsScreen from './screens/SettingsScreen';
import UpgradeGate from './components/UpgradeGate';
import { EntitlementsProvider } from './contexts/EntitlementsContext';
import { useLanguage } from './contexts/LanguageContext';
import {
  consumeLoginLanguageOverride,
  getActiveLanguage,
  normalizeLanguage,
  saveLocalizationPreferences,
} from './localePreference';

// Auth gate + route tree for the job seeker app.
// This component decides whether to show auth, a loading state, or the authenticated workspace.
const DEFAULT_PROFILE = (user) => ({
  username: '',
  email: user.email || '',
  openToWork: true,
  targetRole: '',
  profileLocation: '',
  profileLocationNormalized: '',
  phoneNumber: '',
  bio: '',
  onboardingStatus: 'pending',
  localization: {
    uiLocale: getActiveLanguage(),
    aiLocale: getActiveLanguage(),
    artifactLocale: 'auto',
  },
  source: 'jobseeqr-web-job-seekers',
  createdAt: Date.now(),
});

function applyNestedProfileUpdate(target, path, value) {
  const parts = String(path || '')
    .split('/')
    .filter(Boolean);
  if (!parts.length) return;
  let cursor = target;
  parts.forEach((part, index) => {
    if (index === parts.length - 1) {
      cursor[part] = value;
      return;
    }
    cursor[part] =
      cursor[part] && typeof cursor[part] === 'object' && !Array.isArray(cursor[part])
        ? { ...cursor[part] }
        : {};
    cursor = cursor[part];
  });
}

function materializeProfileUpdates(updates) {
  const materialized = {};
  Object.entries(updates || {}).forEach(([key, value]) => {
    if (key.includes('/')) {
      applyNestedProfileUpdate(materialized, key, value);
    } else {
      materialized[key] = value;
    }
  });
  return materialized;
}

function isPlainObject(value) {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function mergeProfileState(base, updates) {
  const merged = { ...(base || {}) };
  Object.entries(updates || {}).forEach(([key, value]) => {
    if (isPlainObject(value) && isPlainObject(merged[key])) {
      merged[key] = mergeProfileState(merged[key], value);
    } else {
      merged[key] = value;
    }
  });
  return merged;
}

function JobSeekerApp() {
  const location = useLocation();
  const { changeLanguage, language } = useLanguage();
  const activeUserId = useRef(undefined);
  const localeSyncedForUser = useRef('');
  const [authReady, setAuthReady] = useState(false);
  const [profileReady, setProfileReady] = useState(false);
  const [profileError, setProfileError] = useState(false);
  const [profileRetry, setProfileRetry] = useState(0);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      clearCachedFirebaseData();
      setUser(currentUser);
      setAuthReady(true);
      if (activeUserId.current !== (currentUser?.uid || null)) {
        activeUserId.current = currentUser?.uid || null;
        setProfileReady(!currentUser);
        setProfileError(false);
        setProfile(null);
      }

      if (!currentUser) {
        setProfile(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    let revision = 0;
    setProfileReady(false);
    setProfileError(false);
    const profileRef = ref(db, `people/${user.uid}`);
    // Read the routing fields together. Independent field listeners can combine
    // a new completion status with an old onboarding status or missing Scout.
    const unsubscribe = onValue(
      profileRef,
      async (snapshot) => {
        const currentRevision = ++revision;
        try {
          const saved = snapshot.val() || {};
          const base = { ...DEFAULT_PROFILE(user), ...saved };
          const completionPending =
            !hasSavedScout(base) && (await hasPendingOnboardingCompletion(user.uid));
          if (cancelled || currentRevision !== revision) return;
          const nextProfile = completionPending
            ? applyPendingOnboarding(base, {
                onboardingStatus: 'agent_in_progress',
                onboardingCompletionStatus: 'saving',
              })
            : base;
          setProfile(nextProfile);
          setProfileError(false);
          setProfileReady(true);

          // A failed optional backfill must not discard a successfully read account.
          if (snapshot.exists()) {
            const missingFields = {};
            if (!saved.email && nextProfile.email) missingFields.email = nextProfile.email;
            if (!saved.onboardingStatus)
              missingFields.onboardingStatus = hasSavedScout(base)
                ? 'complete'
                : nextProfile.onboardingStatus;
            if (saved.profileLocation === undefined) missingFields.profileLocation = '';
            if (saved.profileLocationNormalized === undefined)
              missingFields.profileLocationNormalized = '';
            if (Object.keys(missingFields).length) {
              update(profileRef, missingFields).catch((error) =>
                console.error('Failed to backfill profile fields:', error),
              );
            }
          }
        } catch (error) {
          if (cancelled || currentRevision !== revision) return;
          console.error('Failed to load job seeker profile:', error);
          setProfileError(true);
          setProfileReady(true);
        }
      },
      (error) => {
        if (cancelled) return;
        revision += 1;
        console.error('Failed to read job seeker profile:', error);
        setProfileError(true);
        setProfileReady(true);
      },
    );
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [user, profileRetry]);

  useEffect(() => {
    if (!user?.uid || !profileReady || localeSyncedForUser.current === user.uid) return;
    const profileLocale = profile?.localization?.uiLocale;
    const loginLanguage = consumeLoginLanguageOverride();

    if (loginLanguage) {
      changeLanguage(loginLanguage);

      if (!profileLocale || normalizeLanguage(profileLocale) !== loginLanguage) {
        const localization = {
          uiLocale: loginLanguage,
          aiLocale: loginLanguage,
          artifactLocale: profile?.localization?.artifactLocale || 'auto',
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
          updatedAt: Date.now(),
        };
        const localeUpdates = {
          'localization/uiLocale': localization.uiLocale,
          'localization/aiLocale': localization.aiLocale,
          'localization/artifactLocale': localization.artifactLocale,
          'localization/timeZone': localization.timeZone,
          'localization/updatedAt': localization.updatedAt,
        };

        saveLocalizationPreferences(user, localization)
          .then(() => {
            setProfile((currentProfile) =>
              mergeProfileState(
                currentProfile || DEFAULT_PROFILE(user),
                materializeProfileUpdates(localeUpdates),
              ),
            );
          })
          .catch((error) => console.error('Failed to save login language preference:', error));
      }
    } else if (profileLocale) {
      changeLanguage(normalizeLanguage(profileLocale));
    }
    localeSyncedForUser.current = user.uid;
  }, [changeLanguage, profile?.localization, profile?.primaryScoutId, profileReady, user]);

  useEffect(() => {
    if (!user) localeSyncedForUser.current = '';
  }, [user]);

  const applyQueuedOnboarding = useCallback(
    (completion) => {
      if (!completion?.queued || !user || activeUserId.current !== user.uid) return;
      setProfile((currentProfile) =>
        applyPendingOnboarding(currentProfile || DEFAULT_PROFILE(user), {
          onboardingStatus: 'agent_in_progress',
          onboardingCompletionStatus: 'queued',
          onboardingCompletionId: completion.accepted?.completionId || completion.id,
        }),
      );
    },
    [user],
  );

  const flushOnboardingOutbox = useCallback(async () => {
    if (!user) return;
    const result = await processPendingOnboardingCompletions(user);
    if (activeUserId.current !== user.uid) return;
    result.completed.forEach(applyQueuedOnboarding);
    if (result.failed.length > 0) {
      setProfile((currentProfile) =>
        applyPendingOnboarding(currentProfile || DEFAULT_PROFILE(user), {
          onboardingStatus: 'agent_in_progress',
          onboardingCompletionStatus: 'retrying',
        }),
      );
    }
  }, [applyQueuedOnboarding, user]);

  useEffect(() => {
    if (!user) return undefined;
    const flush = () => void flushOnboardingOutbox();
    flush();
    const retryTimer = window.setInterval(flush, 30000);
    window.addEventListener('online', flush);
    return () => {
      window.clearInterval(retryTimer);
      window.removeEventListener('online', flush);
    };
  }, [flushOnboardingOutbox, user]);

  const handleOnboardingSubmitted = async (submission) => {
    if (!user) throw new Error('No signed-in user');
    try {
      const id = await queueOnboardingCompletion({ userId: user.uid, ...submission });
      const completion = await processOnboardingCompletion(user, id);
      applyQueuedOnboarding(completion);
      return completion;
    } catch (error) {
      console.error('Onboarding completion failed:', error);
      if (activeUserId.current !== user.uid) throw error;
      setProfile((currentProfile) =>
        applyPendingOnboarding(currentProfile || DEFAULT_PROFILE(user), {
          onboardingStatus: 'agent_in_progress',
          onboardingCompletionStatus: 'retrying',
        }),
      );
      throw error;
    }
  };

  const handleOnboardingExited = () => {
    if (!user || activeUserId.current !== user.uid) return;
    setProfile((currentProfile) =>
      applyPendingOnboarding(currentProfile || DEFAULT_PROFILE(user), {
        onboardingStatus: 'agent_in_progress',
        onboardingCompletionStatus: 'queued',
      }),
    );
  };

  const handleProfileSaved = async (updates, targetUser = user) => {
    if (!targetUser) return;

    const nextUpdates = { ...updates };
    if (Object.prototype.hasOwnProperty.call(nextUpdates, 'profileLocation')) {
      Object.assign(nextUpdates, buildProfileLocationUpdates(nextUpdates.profileLocation));
    }

    // `targetUser` lets the public form persist the first profile immediately
    // after Firebase creates the account, without waiting for the auth observer.
    await update(ref(db, `people/${targetUser.uid}`), nextUpdates);
    if (activeUserId.current !== targetUser.uid) return;
    setProfile((currentProfile) =>
      mergeProfileState(
        currentProfile || DEFAULT_PROFILE(targetUser),
        materializeProfileUpdates(nextUpdates),
      ),
    );
    setProfileReady(true);
  };

  const handleSignOut = async () => {
    await signOut(auth);
  };

  const isPublicApplicationPath = /^\/apply\/[^/]+\/?$/.test(location.pathname);

  if (!authReady || (user && !profileReady && !isPublicApplicationPath)) {
    return <div className="js-app-blank" aria-hidden="true" />;
  }

  // Public applications remain mounted while Firebase creates or reconnects
  // the mandatory account, preventing completed fields and uploads from being lost.
  if (isPublicApplicationPath) {
    return (
      <>
        <SignupAmbassador user={user} />
        <Routes>
          <Route
            path="/apply/:jobId"
            element={
              <PublicJobApplicationScreen
                user={user}
                profile={profile || {}}
                onProfileSaved={handleProfileSaved}
              />
            }
          />
        </Routes>
      </>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<Navigate to="/sign-up" replace />} />
        <Route path="/sign-up" element={<AuthScreen mode="signup" />} />
        <Route path="/log-in" element={<AuthScreen mode="login" />} />
        <Route path="/reset-password" element={<AuthScreen mode="reset" />} />
        <Route path="*" element={<Navigate to="/sign-up" replace />} />
      </Routes>
    );
  }

  if (profileError) {
    return (
      <main className="js-onboarding is-name-step">
        <section className="js-onboarding__card is-compact" role="alert">
          <h1>{language === 'ar' ? 'تعذّر تحميل حسابك' : 'We couldn’t load your account'}</h1>
          <p>
            {language === 'ar'
              ? 'حاول مجدداً للمتابعة من حيث توقفت.'
              : 'Try again to pick up where you left off.'}
          </p>
          <button
            className="js-onboarding__button"
            onClick={() => setProfileRetry((count) => count + 1)}
          >
            {language === 'ar' ? 'حاول مجدداً' : 'Try again'}
          </button>
          <button className="js-onboarding__text-button" onClick={handleSignOut}>
            {language === 'ar' ? 'تسجيل الخروج' : 'Sign out'}
          </button>
        </section>
      </main>
    );
  }

  if (needsOnboarding(profile) && !isBasicJobSearchPath(location.pathname)) {
    return (
      <>
        <SignupAmbassador user={user} />
        <Routes>
          <Route
            path="/outreach-email-callback"
            element={<OutreachEmailCallback user={user} onProfileSaved={handleProfileSaved} />}
          />
          <Route
            path="/onboarding"
            element={
              <OnboardingScreen
                profile={profile}
                user={user}
                onProfileSaved={handleProfileSaved}
                onOnboardingSubmitted={handleOnboardingSubmitted}
                onOnboardingExited={handleOnboardingExited}
              />
            }
          />
          <Route path="*" element={<Navigate to="/onboarding" replace />} />
        </Routes>
      </>
    );
  }

  return (
    <EntitlementsProvider user={user}>
      <SignupAmbassador user={user} />
      <Routes>
        <Route
          element={
            <JobSeekerShell
              profile={profile}
              user={user}
              onProfileSaved={handleProfileSaved}
              onSignOut={handleSignOut}
            />
          }
        >
          <Route path="/" element={<Navigate to="/opportunities" replace />} />
          <Route
            path="/opportunities"
            element={<OpportunitiesScreen user={user} profile={profile} />}
          />
          <Route
            path="/outreach-email-callback"
            element={<OutreachEmailCallback user={user} onProfileSaved={handleProfileSaved} />}
          />
          <Route path="/job/:jobId" element={<JobSearchScreen user={user} profile={profile} />} />
          <Route path="/inbox" element={<InboxScreen user={user} />} />
          <Route path="/outreach" element={<InboxScreen user={user} initialTab="outreach" />} />
          <Route
            path="/career-coach"
            element={<CareerCoachScreen user={user} profile={profile} />}
          />
          <Route
            path="/career-paths"
            element={<CareerPathsScreen user={user} profile={profile} />}
          />
          <Route
            path="/ask-anything"
            element={<AskAnythingScreen user={user} onProfileSaved={handleProfileSaved} />}
          />
          <Route
            path="/scout-details"
            element={<ConfigureScoutScreen user={user} profile={profile} detailsOnly />}
          />
          <Route
            path="/configure-scout"
            element={
              <UpgradeGate user={user} context="scout_edit">
                <ConfigureScoutScreen
                  user={user}
                  profile={profile}
                  onProfileSaved={handleProfileSaved}
                />
              </UpgradeGate>
            }
          />
          <Route
            path="/mock-interview"
            element={
              <MockInterviewScreen key="mock-interview-practice" user={user} profile={profile} />
            }
          />
          <Route
            path="/mock-interview/reports"
            element={
              <UpgradeGate user={user} context="interview">
                <MockInterviewScreen
                  key="mock-interview-reports"
                  user={user}
                  profile={profile}
                  reportsOnly
                />
              </UpgradeGate>
            }
          />
          <Route
            path="/mock-interview/reports/:reportId"
            element={
              <UpgradeGate user={user} context="interview">
                <MockInterviewScreen
                  key="mock-interview-report-detail"
                  user={user}
                  profile={profile}
                  reportsOnly
                />
              </UpgradeGate>
            }
          />
          <Route path="/company-radar" element={<CompanyRadarScreen user={user} />} />
          <Route path="/company-radar/:companyId" element={<CompanyRadarScreen user={user} />} />
          <Route
            path="/learning-pathways"
            element={<LearningPathwaysScreen user={user} profile={profile} />}
          />
          <Route path="/notifications" element={<Navigate to="/inbox" replace />} />
          <Route
            path="/profile"
            element={
              <ProfileScreen
                profile={profile}
                user={user}
                onProfileSaved={handleProfileSaved}
                onSignOut={handleSignOut}
              />
            }
          />
          <Route
            path="/settings"
            element={
              <SettingsScreen profile={profile} user={user} onProfileSaved={handleProfileSaved} />
            }
          />
          <Route path="*" element={<Navigate to="/opportunities" replace />} />
        </Route>
      </Routes>
    </EntitlementsProvider>
  );
}

export default JobSeekerApp;
